import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { Camera, X, MapPin, ClipboardCheck, Trash2 } from "lucide-react";
import { itemsFor, CarDamageDiagram } from "./VehicleDamageDiagram";

// Delivery inspection (فحص التسليم): parts checklist + interactive damage diagram + photos + note.
// Exposes getInspection() and getPhotos() via ref (like SignaturePad) so parent dialogs stay simple.

const MAX_DIM = 1280;
export const fileToDataUrl = (file) => new Promise((resolve) => {
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > MAX_DIM || height > MAX_DIM) {
        const s = MAX_DIM / Math.max(width, height);
        width = Math.round(width * s); height = Math.round(height * s);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", 0.82));
    };
    img.onerror = () => resolve(reader.result);
    img.src = reader.result;
  };
  reader.readAsDataURL(file);
});

export const VehicleInspectionForm = forwardRef(({ stage = "التسليم", kind: initialKind = "car", noPhotos = false, noOdometer = false }, ref) => {
  const [kind, setKind] = useState(initialKind === "moto" ? "moto" : "car");
  const [checklist, setChecklist] = useState({});
  const [damages, setDamages] = useState([]); // {x,y,note}
  const [photos, setPhotos] = useState([]);    // dataURLs
  const [note, setNote] = useState("");
  const [odometer, setOdometer] = useState("");
  const diagramRef = useRef(null);
  const fileRef = useRef(null);
  const lastClickRef = useRef(0);

  useImperativeHandle(ref, () => ({
    getInspection: () => {
      const hasAny = Object.keys(checklist).length || damages.length || note.trim() || odometer || photos.length;
      if (!hasAny) return null;
      return {
        kind,
        checklist: Object.fromEntries(Object.entries(checklist).filter(([k]) => itemsFor(kind).some((i) => i.key === k))),
        damages: damages.map((d) => ({ x: d.x, y: d.y, note: (d.note || "").trim() })),
        note: note.trim(),
        odometer: odometer ? Number(odometer) : null,
      };
    },
    getPhotos: () => photos,
  }), [kind, checklist, damages, note, odometer, photos]);

  const toggle = (k) => setChecklist((c) => ({ ...c, [k]: !c[k] }));

  const addMarker = (e) => {
    const now = Date.now();
    if (now - lastClickRef.current < 250) return; // guard against double-fire
    lastClickRef.current = now;
    const rect = diagramRef.current.getBoundingClientRect();
    const x = Math.round(((e.clientX - rect.left) / rect.width) * 1000) / 10;
    const y = Math.round(((e.clientY - rect.top) / rect.height) * 1000) / 10;
    setDamages((d) => [...d, { x, y, note: "" }]);
  };
  const removeMarker = (i) => setDamages((d) => d.filter((_, idx) => idx !== i));
  const setMarkerNote = (i, v) => setDamages((d) => d.map((m, idx) => (idx === i ? { ...m, note: v } : m)));

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) {
      if (!f.type.startsWith("image/")) continue;
      const url = await fileToDataUrl(f);
      setPhotos((p) => [...p, url]);
    }
    if (fileRef.current) fileRef.current.value = "";
  };
  const removePhoto = (i) => setPhotos((p) => p.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-4 rounded-xl border border-slate-200 bg-slate-50/60 p-3" data-testid="vehicle-inspection-form">
      <p className="text-sm font-bold text-slate-700 flex items-center gap-1.5">
        <ClipboardCheck className="h-4 w-4 text-emerald-600" /> فحص {stage}
      </p>
      <div className="flex gap-1.5" data-testid="insp-kind">
        {[["car", "سيارة"], ["moto", "دراجة نارية"]].map(([k, l]) => (
          <button key={k} type="button" onClick={() => { if (k !== kind) { setKind(k); setDamages([]); } }} data-testid={`insp-kind-${k}`}
            className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${kind === k ? "bg-emerald-600 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-100"}`}>{l}</button>))}
      </div>

      {/* Checklist */}
      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
        {itemsFor(kind).map((it) => (
          <label key={it.key} data-testid={`insp-check-${it.key}`}
            className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer select-none">
            <input type="checkbox" checked={!!checklist[it.key]} onChange={() => toggle(it.key)}
              className="h-4 w-4 rounded accent-emerald-600" />
            {it.label}
          </label>
        ))}
      </div>

      {/* Odometer */}
      {!noOdometer && <div className="space-y-1">
        <label className="text-xs font-semibold text-slate-600">قراءة العدّاد (كم) — اختياري</label>
        <input type="number" inputMode="numeric" dir="ltr" data-testid="insp-odometer"
          value={odometer} onChange={(e) => setOdometer(e.target.value)}
          className="w-full h-9 rounded-lg border border-slate-200 px-2 text-sm font-mono-nums" placeholder="0" />
      </div>}

      {/* Damage diagram */}
      <div className="space-y-1.5">
        <p className="text-xs font-semibold text-slate-600 flex items-center gap-1">
          <MapPin className="h-3.5 w-3.5 text-rose-500" /> مخطط الأضرار — {kind === "moto" ? "اضغط على الدراجة لتحديد موضع الضرر" : "اضغط على السيارة لتحديد موضع الضرر"}
        </p>
        <CarDamageDiagram kind={kind} damages={damages} interactive diagramRef={diagramRef} onAdd={addMarker} onRemove={removeMarker} />
        {damages.length > 0 && (
          <div className="space-y-1.5">
            {damages.map((d, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="h-5 w-5 shrink-0 rounded-full bg-rose-600 text-white text-[11px] grid place-items-center font-bold">{i + 1}</span>
                <input value={d.note} onChange={(e) => setMarkerNote(i, e.target.value)}
                  data-testid={`insp-marker-note-${i}`}
                  placeholder="وصف الضرر (خدش، صدمة…)" className="flex-1 h-8 rounded-lg border border-slate-200 px-2 text-xs" />
                <button type="button" onClick={() => removeMarker(i)} className="text-rose-500 p-1"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Photos */}
      {!noPhotos && <div className="space-y-1.5">
        <p className="text-xs font-semibold text-slate-600">صور المركبة عند {stage}</p>
        <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple hidden
          data-testid="insp-photo-input" onChange={onFiles} />
        <button type="button" onClick={() => fileRef.current?.click()} data-testid="insp-photo-btn"
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-blue-200 text-blue-700 hover:bg-blue-50 text-sm font-medium">
          <Camera className="h-4 w-4" /> تصوير / إضافة صور
        </button>
        {photos.length > 0 && (
          <div className="grid grid-cols-3 gap-2 pt-1">
            {photos.map((src, i) => (
              <div key={i} className="relative">
                <img src={src} alt="" className="h-20 w-full object-cover rounded-lg border border-slate-200" />
                <button type="button" data-testid={`insp-photo-remove-${i}`} onClick={() => removePhoto(i)}
                  className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-rose-600 text-white grid place-items-center shadow">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>}

      {/* Note */}
      <div className="space-y-1">
        <label className="text-xs font-semibold text-slate-600">ملاحظة حالة {stage}</label>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="insp-note" rows={2}
          className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm" placeholder="أي ملاحظات عامة عن حالة المركبة…" />
      </div>
    </div>
  );
});

VehicleInspectionForm.displayName = "VehicleInspectionForm";
